import { Chart as ChartJS } from 'chart.js/auto';

// Add type declaration for window.Chart
declare global {
  interface Window {
    Chart?: any;
  }
}

// Ensure Chart.js is available globally for inline scripts
if (typeof window !== 'undefined') {
  window.Chart = ChartJS;
}

// Выполнение скриптов для инициализации графиков
export const executeChartScripts = (container: HTMLElement, contextPrefix: string = '') => {
  console.log('[CHART_RENDERER] executeChartScripts - начало выполнения, container:', container, 'contextPrefix:', contextPrefix);
  
  // Проверяем наличие Chart.js
  if (!window.Chart) {
    console.error('[CHART_RENDERER] executeChartScripts - Chart.js не найден в глобальном объекте window');
    console.log('[CHART_RENDERER] executeChartScripts - пытаемся загрузить Chart.js динамически');
    
    // Пытаемся загрузить Chart.js динамически
    const chartScript = document.createElement('script');
    chartScript.src = 'https://cdn.jsdelivr.net/npm/chart.js';
    chartScript.onload = () => {
      console.log('[CHART_RENDERER] executeChartScripts - Chart.js успешно загружен, повторяем выполнение');
      // Повторяем выполнение после загрузки Chart.js
      setTimeout(() => executeChartScripts(container), 100);
    };
    chartScript.onerror = (error) => {
      console.error('[CHART_RENDERER] executeChartScripts - ошибка при загрузке Chart.js:', error);
    };
    document.head.appendChild(chartScript);
    return;
  }
  
  // Ищем скрипты в контейнере
  const scripts = container.querySelectorAll('script');
  console.log(`[CHART_RENDERER] executeChartScripts - найдено ${scripts.length} скриптов`);
  
  scripts.forEach((script, index) => {
    // Объявляем переменные вне блока try, чтобы они были доступны в блоке catch
    let originalChartHtml = '';
    let scriptText = '';
    let uniqueId = '';
    let configString = '';
    
    console.log(`[CHART_RENDERER] executeChartScripts - обработка скрипта #${index}`);
    
    try {
      // Найти родительский div с canvas элементом
      const parentDiv = script.parentElement;
      if (!parentDiv) {
        console.warn(`[CHART_RENDERER] executeChartScripts - скрипт #${index} не имеет родительского элемента`);
        return;
      }
      
      const canvas = parentDiv.querySelector('canvas');
      if (!canvas) {
        console.warn(`[CHART_RENDERER] executeChartScripts - не найден canvas для скрипта #${index}`);
        return;
      }
      
      // Сохраняем оригинальный ID канваса
      const originalId = canvas.id;
      console.log(`[CHART_RENDERER] executeChartScripts - оригинальный ID канваса: "${originalId}"`);
      
      // Извлекаем ID из скрипта
      const scriptContent = script.textContent || '';
      const idMatch = scriptContent.match(/getElementById\(['"]([^'"]+)['"]\)/);
      const scriptId = idMatch ? idMatch[1] : null;
      console.log(`[CHART_RENDERER] executeChartScripts - ID из скрипта: "${scriptId}"`);
      
      // Проверяем на несоответствие ID
      if (originalId && scriptId && originalId !== scriptId) {
        console.error('[CHART_RENDERER] ID mismatch detected between canvas and script!');
        console.error('[CHART_RENDERER] Canvas ID:', originalId);
        console.error('[CHART_RENDERER] Script references ID:', scriptId);
        console.error('[CHART_RENDERER] Original script content:', scriptContent);
        
        // Используем ID из canvas для обновления скрипта
        const fixedScript = scriptContent.replace(
          new RegExp(`getElementById\\(['"]${scriptId}['"]\\)`), 
          `getElementById('${originalId}')`
        );
        
        console.log('[CHART_RENDERER] executeChartScripts - исправленный скрипт:', fixedScript);
        
        // Создаем новый скрипт с исправленным ID
        const fixScript = document.createElement('script');
        fixScript.textContent = fixedScript;
        
        // Заменяем оригинальный скрипт исправленным
        script.parentNode?.replaceChild(fixScript, script);
        console.log('[CHART_RENDERER] executeChartScripts - скрипт заменен на исправленный');
        
        // Продолжаем с исправленным ID
        uniqueId = originalId;
      } else {
        // Создаем уникальный ID для канваса без использования timestamp
        chartIdCounter++;
        // Используем контекстный префикс, если он предоставлен
        uniqueId = contextPrefix ? `${contextPrefix}-chart-${index}-${chartIdCounter}` : `chart-${index}-${chartIdCounter}`;
        console.log(`[CHART_RENDERER] executeChartScripts - создан новый ID: "${uniqueId}", contextPrefix: "${contextPrefix}"`);
        canvas.id = uniqueId;
      }
      
      // Сохраняем оригинальный HTML скрипта
      originalChartHtml = script.textContent || '';
      
      // Сначала извлекаем конфигурацию из оригинального скрипта
      let chartConfig = null;
      const originalScript = script.textContent || '';

      try {
        console.log('[CHART_RENDERER] executeChartScripts - извлечение конфигурации из скрипта');
        // Apply LLM error repair before extraction
        const repairedScript = repairDatasetBraces(originalScript);
        configString = extractConfigString(repairedScript) || '';

        if (configString) {
          console.log('[CHART_RENDERER] executeChartScripts - извлеченная строка конфигурации:',
            configString.length > 100 ? configString.substring(0, 100) + '...' : configString);

          chartConfig = tryParseConfig(configString);
          console.log('[CHART_RENDERER] executeChartScripts - конфигурация успешно извлечена:',
            chartConfig ? 'тип: ' + chartConfig.type : 'конфигурация пуста');
        }
      } catch (parseError) {
        console.error('[CHART_RENDERER] Error parsing chart config:', parseError);
        console.error('[CHART_RENDERER] Original script content:', originalScript);
      }
      
      // Создаем новый скрипт с обновленным ID канваса
      const newScript = document.createElement('script');
      
      // Используем извлеченную конфигурацию или дефолтную, если не удалось извлечь
      const defaultConfig = {
        type: 'bar',
        data: {
          labels: [],
          datasets: []
        },
        options: {
          responsive: true,
          maintainAspectRatio: false
        }
      };
      
      const finalConfig = chartConfig || defaultConfig;

      // Fix matplotlib-style 'barh' type → 'bar' + indexAxis: 'y'
      if (finalConfig.type === 'barh') {
        console.log('[CHART_RENDERER] Converting barh → bar with indexAxis: y');
        finalConfig.type = 'bar';
        finalConfig.options = finalConfig.options || {};
        if (!finalConfig.options.indexAxis) {
          finalConfig.options.indexAxis = 'y';
        }
      }

      // Fix deprecated Chart.js v2 'horizontalBar' type
      if (finalConfig.type === 'horizontalBar') {
        console.log('[CHART_RENDERER] Converting horizontalBar → bar with indexAxis: y');
        finalConfig.type = 'bar';
        finalConfig.options = finalConfig.options || {};
        if (!finalConfig.options.indexAxis) {
          finalConfig.options.indexAxis = 'y';
        }
      }

      // Position legend to the right for circular charts to prevent vertical overflow
      if (finalConfig.type && ['pie', 'doughnut', 'polarArea'].includes(finalConfig.type)) {
        finalConfig.options = finalConfig.options || {};
        finalConfig.options.plugins = finalConfig.options.plugins || {};
        finalConfig.options.plugins.legend = {
          ...(finalConfig.options.plugins.legend || {}),
          position: 'right'
        };
      }

      console.log('[CHART_RENDERER] executeChartScripts - финальная конфигурация:',
        finalConfig ? 'тип: ' + finalConfig.type : 'конфигурация пуста');
      
      // Создаем скрипт, который уничтожает предыдущий экземпляр и создает новый с правильной конфигурацией
      scriptText = `
// Логирование начала выполнения скрипта
console.log('[CHART_SCRIPT] Начало выполнения скрипта для графика с ID: ${uniqueId}');

// Проверяем наличие Chart.js
if (!window.Chart) {
  console.error('[CHART_SCRIPT] Chart.js не найден в window.Chart!');
} else {
  console.log('[CHART_SCRIPT] Chart.js найден, версия:', window.Chart.version || 'неизвестна');
}

  // Проверяем наличие элемента canvas и сохраняем его в переменную
  var canvasEl = document.getElementById('${uniqueId}');
  if (!canvasEl) {
    console.error('[CHART_SCRIPT] Canvas элемент с ID ${uniqueId} не найден!');
  } else {
    console.log('[CHART_SCRIPT] Canvas элемент найден:', canvasEl);
  }
  
  // Уничтожаем предыдущий экземпляр графика, если он существует
  var existingChart = window.Chart ? window.Chart.getChart('${uniqueId}') : null;
  if (existingChart) {
    console.log('[CHART_SCRIPT] Найден существующий график с ID ${uniqueId}, уничтожаем');
    existingChart.destroy();
  } else {
    console.log('[CHART_SCRIPT] Существующий график с ID ${uniqueId} не найден');
  }
  
  // Создаем новый график с правильным ID и полной конфигурацией
  if (window.Chart && canvasEl) {
  console.log('[CHART_SCRIPT] Создаем новый график с ID ${uniqueId}');
  try {
    const ctx = canvasEl.getContext('2d');
    if (!ctx) {
      console.error('[CHART_SCRIPT] Не удалось получить 2d контекст для canvas');
    } else {
      const chartInstance = new window.Chart(ctx, ${JSON.stringify(finalConfig)});
      console.log('[CHART_SCRIPT] График успешно создан:', chartInstance);
    }
  } catch (error) {
    console.error('[CHART_SCRIPT] Ошибка при создании графика:', error);
  }
}
      `;
      
      // Устанавливаем содержимое скрипта
      newScript.textContent = scriptText;
      console.log('[CHART_RENDERER] executeChartScripts - создан новый скрипт');
      
      // Добавляем скрипт в документ
      document.body.appendChild(newScript);
      console.log('[CHART_RENDERER] executeChartScripts - скрипт добавлен в документ');
    } catch (error) {
      console.error('[CHART_RENDERER] Error processing chart script:', error);
      // Логируем исходный HTML и сгенерированный код при ошибке
      console.error('[CHART_RENDERER] Original chart HTML:', originalChartHtml);
      console.error('[CHART_RENDERER] Generated chart code:', scriptText);
    }
  });
  
  console.log('[CHART_RENDERER] executeChartScripts - завершение выполнения');
};

// Fix LLM error: premature closing brace between data array and dataset styling properties
// Example: data:[{x:1},{x:2}]}, backgroundColor:'red' → data:[{x:1},{x:2}], backgroundColor:'red'
// The LLM sometimes closes the dataset object right after data:[], leaving backgroundColor etc. orphaned
const repairDatasetBraces = (script: string): string => {
  const datasetProps = [
    'backgroundColor', 'borderColor', 'borderWidth', 'pointRadius', 'pointStyle',
    'fill', 'tension', 'borderDash', 'hoverBackgroundColor', 'hoverBorderColor',
    'showLine', 'spanGaps', 'barThickness', 'borderRadius', 'pointHoverRadius',
    'pointBackgroundColor', 'pointBorderColor', 'pointBorderWidth', 'order',
    'borderDashOffset', 'pointHitRadius', 'pointHoverBackgroundColor',
    'pointHoverBorderColor', 'pointHoverBorderWidth', 'barPercentage',
    'categoryPercentage', 'minBarLength', 'maxBarThickness', 'borderSkipped',
    'clip', 'hidden', 'label', 'stack', 'yAxisID', 'xAxisID'
  ].join('|');

  const pattern = new RegExp(`(\\])\\s*\\}\\s*,\\s*(${datasetProps})\\s*:`, 'g');
  const repaired = script.replace(pattern, '$1,$2:');

  if (repaired !== script) {
    console.log('[CHART_RENDERER] repairDatasetBraces - исправлена преждевременная закрывающая скобка в конфигурации датасета');
  }

  return repaired;
};

// Поиск соответствующей закрывающей скобки с учетом строковых литералов
const findMatchingBrace = (str: string, openBraceIndex: number): number => {
  let braceCount = 1;
  let inSingleQuote = false;
  let inDoubleQuote = false;
  let inBacktick = false;
  let escaped = false;

  for (let i = openBraceIndex + 1; i < str.length; i++) {
    const ch = str[i];

    if (escaped) {
      escaped = false;
      continue;
    }

    if (ch === '\\') {
      escaped = true;
      continue;
    }

    if (ch === "'" && !inDoubleQuote && !inBacktick) {
      inSingleQuote = !inSingleQuote;
      continue;
    }

    if (ch === '"' && !inSingleQuote && !inBacktick) {
      inDoubleQuote = !inDoubleQuote;
      continue;
    }

    if (ch === '`' && !inSingleQuote && !inDoubleQuote) {
      inBacktick = !inBacktick;
      continue;
    }

    if (inSingleQuote || inDoubleQuote || inBacktick) {
      continue;
    }

    if (ch === '{') {
      braceCount++;
    } else if (ch === '}') {
      braceCount--;
      if (braceCount === 0) {
        return i;
      }
    }
  }

  return -1;
};

// Extract config string from script content using brace matching, with end-anchor fallback
const extractConfigString = (scriptContent: string): string | null => {
  const chartCallIndex = scriptContent.indexOf('new Chart');
  if (chartCallIndex === -1) return null;

  const commaIndex = scriptContent.indexOf(',', chartCallIndex);
  if (commaIndex === -1) return null;

  const openBraceIndex = scriptContent.indexOf('{', commaIndex);
  if (openBraceIndex === -1) return null;

  // Try 1: findMatchingBrace (handles string literals properly)
  let closeBraceIndex = findMatchingBrace(scriptContent, openBraceIndex);
  if (closeBraceIndex !== -1) {
    return scriptContent.substring(openBraceIndex, closeBraceIndex + 1);
  }

  // Try 2: end-anchor — take everything from '{' to just before the final ');' of the Chart call
  const endMatch = scriptContent.match(/\)\s*;?\s*$/);
  if (endMatch && endMatch.index != null && endMatch.index > openBraceIndex) {
    let configString = scriptContent.substring(openBraceIndex, endMatch.index);
    // Auto-complete missing closing braces (up to 3)
    let braceCount = 0;
    let inSQ = false, inDQ = false, escaped = false;
    for (let i = 0; i < configString.length; i++) {
      const ch = configString[i];
      if (escaped) { escaped = false; continue; }
      if (ch === '\\') { escaped = true; continue; }
      if (ch === "'" && !inDQ) { inSQ = !inSQ; continue; }
      if (ch === '"' && !inSQ) { inDQ = !inDQ; continue; }
      if (inSQ || inDQ) continue;
      if (ch === '{') braceCount++;
      else if (ch === '}') braceCount--;
    }
    if (braceCount > 0 && braceCount <= 3) {
      console.warn(`[extractChartConfig] End-anchor: auto-completing ${braceCount} missing brace(s)`);
      configString += '}'.repeat(braceCount);
    }
    return configString;
  }

  return null;
};

// Try to parse a config string via new Function, return config object or null
const tryParseConfig = (configString: string): any => {
  const configCode = `return ${configString};`;
  const configFunction = new Function(configCode);
  return configFunction();
};

// Извлечение конфигурации графика из HTML
export const extractChartConfig = (chartHtml: string): any => {
  try {
    // Извлекаем содержимое script из HTML
    let scriptContent = chartHtml;
    const scriptStartIndex = chartHtml.indexOf('<script>');
    const scriptEndIndex = chartHtml.indexOf('</script>');
    if (scriptStartIndex !== -1 && scriptEndIndex !== -1) {
      scriptContent = chartHtml.substring(scriptStartIndex + 8, scriptEndIndex);
    }

    if (scriptContent.indexOf('new Chart') === -1) {
      return null;
    }

    // Apply LLM error repair before extraction
    const repairedScript = repairDatasetBraces(scriptContent);

    // Extract config string (brace-matching, then end-anchor fallback)
    const configString = extractConfigString(repairedScript);
    if (!configString) return null;

    try {
      const config = tryParseConfig(configString);
      return {
        type: config.type || 'bar',
        labels: config.data?.labels || [],
        datasets: config.data?.datasets || [],
        options: config.options || {}
      };
    } catch (parseError) {
      // If repaired script still fails, try with the original (unrepaired) as last resort
      if (repairedScript !== scriptContent) {
        const origConfigString = extractConfigString(scriptContent);
        if (origConfigString) {
          try {
            const config = tryParseConfig(origConfigString);
            return {
              type: config.type || 'bar',
              labels: config.data?.labels || [],
              datasets: config.data?.datasets || [],
              options: config.options || {}
            };
          } catch (_e) { /* fall through */ }
        }
      }
      console.error('Error parsing chart config:', parseError);
      console.error('Original chart HTML:', chartHtml);
      console.error('Script content:', scriptContent);
      console.error('Config string being parsed:', configString);
      return null;
    }
  } catch (error) {
    console.error('Error extracting chart config:', error);
    console.error('Original chart HTML:', chartHtml);
    return null;
  }
};

// Инициализация графиков
export const initCharts = () => {
  const chartCanvases = document.querySelectorAll('canvas[data-chart]');
  
  chartCanvases.forEach((canvas: Element, index: number) => {
    try {
      const chartData = JSON.parse((canvas as HTMLElement).getAttribute('data-chart') || '{}');
      renderChart(canvas as HTMLCanvasElement, chartData);
    } catch (error) {
      console.error(`Error initializing chart #${index}:`, error);
      showChartError(canvas as HTMLCanvasElement, error as Error);
    }
  });
};

// Рендеринг графика
export const renderChart = (canvas: HTMLCanvasElement, chartData: any) => {
  if (!chartData || !chartData.type) {
    throw new Error('Invalid chart data: missing type');
  }

  const options = chartData.options || {
    responsive: true,
    maintainAspectRatio: false
  };

  // Fix matplotlib-style 'barh' and deprecated 'horizontalBar' types
  let chartType = chartData.type;
  if (chartType === 'barh' || chartType === 'horizontalBar') {
    chartType = 'bar';
    options.indexAxis = options.indexAxis || 'y';
  }

  // Position legend to the right for circular charts to prevent vertical overflow
  if (['pie', 'doughnut', 'polarArea'].includes(chartType)) {
    options.plugins = options.plugins || {};
    options.plugins.legend = {
      ...(options.plugins.legend || {}),
      position: 'right'
    };
  }

  // Create chart configuration
  const config = {
    type: chartType,
    data: {
      labels: chartData.labels || [],
      datasets: chartData.datasets || []
    },
    options
  };

  // Create chart
  new ChartJS(canvas, config);
};

// Отображение ошибки графика
export const showChartError = (canvas: HTMLCanvasElement, error: Error) => {
  // Replace canvas with error message
  const errorDiv = document.createElement('div');
  errorDiv.className = 'chart-error';
  errorDiv.innerHTML = `
    <div class="error-icon">
      <i class="fas fa-exclamation-triangle"></i>
    </div>
    <div class="error-message">
      <h4>Ошибка при загрузке графика</h4>
      <p>${error.message}</p>
    </div>
  `;
  
  if (canvas.parentNode) {
    canvas.parentNode.replaceChild(errorDiv, canvas);
  }
};

// Счетчик для генерации уникальных ID
let chartIdCounter = 0;

// Создание HTML для графика
export const createChartHtml = (type: string, title: string, labels: any[], values: any[], contextPrefix: string = ''): string => {
  // Создаем простой и стабильный ID без использования timestamp
  // Используем инкрементальный счетчик для обеспечения уникальности
  chartIdCounter++;
  const uniqueId = contextPrefix ? `${contextPrefix}-chart-${chartIdCounter}` : `chart-${chartIdCounter}`;
  console.log(`[CHART_RENDERER] createChartHtml - создан ID для графика: "${uniqueId}", contextPrefix: "${contextPrefix}"`);
  
  // Escape special characters in title to prevent JavaScript syntax errors
  const escapedTitle = title.replace(/'/g, "\\'").replace(/"/g, '\\"');
  
  console.log(`[CHART_RENDERER] createChartHtml - тип графика: ${type}, заголовок: ${escapedTitle}`);
  console.log(`[CHART_RENDERER] createChartHtml - метки:`, labels);
  console.log(`[CHART_RENDERER] createChartHtml - значения:`, values);
  
  // Создаем конфигурацию графика для атрибута data-chart
  const isPieType = ['pie', 'doughnut', 'polarArea'].includes(type);
  const chartConfig = {
    type: type,
    labels: labels,
    datasets: [{
      label: type === 'bar' ? escapedTitle : '',
      data: values,
      backgroundColor: ['rgba(54, 162, 235, 0.7)', 'rgba(255, 99, 132, 0.7)', 'rgba(255, 206, 86, 0.7)',
                        'rgba(75, 192, 192, 0.7)', 'rgba(153, 102, 255, 0.7)'],
      borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)', 'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)', 'rgba(153, 102, 255, 1)'],
      borderWidth: 1
    }],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: escapedTitle
        },
        ...(isPieType ? { legend: { position: 'right' } } : {})
      }
    }
  };
  
  // Важно: ID в canvas и в getElementById должны совпадать!
  return `<div class="chart-wrapper" style="margin-bottom: 20px; clear: both;">
  <div class="chart-container" style="position: relative; width: 100%; height: 400px;">
    <canvas id="${uniqueId}" data-chart='${JSON.stringify(chartConfig)}'></canvas>
  </div>
  <script>
  // Логирование для отладки
  console.log('[CHART_HTML] Начало выполнения скрипта для графика ${uniqueId}');
  console.log('[CHART_HTML] Тип графика: ${type}');
  console.log('[CHART_HTML] Заголовок: ${escapedTitle}');
  console.log('[CHART_HTML] Метки:', ${JSON.stringify(labels)});
  console.log('[CHART_HTML] Значения:', ${JSON.stringify(values)});
  
  // Проверяем наличие Chart.js
  if (!window.Chart) {
    console.error('[CHART_HTML] Chart.js не найден в window.Chart!');
  } else {
    console.log('[CHART_HTML] Chart.js найден, версия:', window.Chart.version || 'неизвестна');
  }
  
  // Проверяем наличие элемента canvas
  var canvasEl = document.getElementById('${uniqueId}');
  if (!canvasEl) {
    console.error('[CHART_HTML] Canvas элемент с ID ${uniqueId} не найден!');
  } else {
    console.log('[CHART_HTML] Canvas элемент найден:', canvasEl);
  }
  
  // ВАЖНО: Используем точно такой же ID, как у canvas элемента выше
  try {
    if (window.Chart && canvasEl) {
      const ctx = canvasEl.getContext('2d');
      if (!ctx) {
        console.error('[CHART_HTML] Не удалось получить 2d контекст для canvas');
      } else {
        const chartInstance = new Chart(ctx, {
          type: '${type}',
          data: {
            labels: ${JSON.stringify(labels)},
            datasets: [{
              ${type === 'bar' ? `label: '${escapedTitle}',` : ''}
              data: ${JSON.stringify(values)},
              backgroundColor: ['rgba(54, 162, 235, 0.7)', 'rgba(255, 99, 132, 0.7)', 'rgba(255, 206, 86, 0.7)', 'rgba(75, 192, 192, 0.7)', 'rgba(153, 102, 255, 0.7)'],
              borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)', 'rgba(255, 206, 86, 1)', 'rgba(75, 192, 192, 1)', 'rgba(153, 102, 255, 1)'],
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: true,
                text: '${escapedTitle}'
              }${isPieType ? `,
              legend: {
                position: 'right'
              }` : ''}
            }
          }
        });
        console.log('[CHART_HTML] График успешно создан:', chartInstance);
      }
    }
  } catch (error) {
    console.error('[CHART_HTML] Ошибка при создании графика:', error);
  }
  </script>
</div>`;
};
